import {
  keepPreviousData,
  skipToken,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import type { HistogramData, HistogramDataItem } from '../../types/domain';
import {
  dataApiClient,
  type ApiDataFilter,
  type TripHistogramType,
} from '../../infrastructure/dataApi/client';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import {
  useDataFilter,
  type DataFilter,
  type RangeFilterType,
} from '../SimViewer/useDataFilter';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { getNiceHistogramBucket, type ValueType } from '../../utils/chart';
import { isEmptyFilter } from '../../utils/filter';

type UseTripHistogramData = {
  isLoading: boolean;
  mean: number | null;
  median: number | null;
  items: HistogramDataItem[];
};

type HistogramConfig = {
  valueType: ValueType;
  rangeFilterType: RangeFilterType;
  apiFilterKey: keyof ApiDataFilter;
};

const EMPTY_DATA = [] as const satisfies HistogramDataItem[];

const HISTOGRAM_CONFIGS = {
  traveled_time: {
    valueType: 'duration',
    rangeFilterType: 'traveledTime',
    apiFilterKey: 'traveled_time',
  },
  traveled_distance: {
    valueType: 'distance',
    rangeFilterType: 'traveledDistance',
    apiFilterKey: 'traveled_distance',
  },
  activity_time: {
    valueType: 'duration',
    rangeFilterType: 'activityTime',
    apiFilterKey: 'activity_time',
  },
  dep_time: {
    valueType: 'time',
    rangeFilterType: 'depTime',
    apiFilterKey: 'dep_time',
  },
  arr_time: {
    valueType: 'time',
    rangeFilterType: 'arrTime',
    apiFilterKey: 'arr_time',
  },
  passage_time: {
    valueType: 'time',
    rangeFilterType: 'passageTime',
    apiFilterKey: 'passage_time',
  },
  staying_population: {
    valueType: 'time',
    rangeFilterType: 'stayingTime',
    apiFilterKey: 'staying_time',
  },
} as const satisfies Record<TripHistogramType, HistogramConfig>;

const shouldFetch = (
  type: TripHistogramType,
  dataFilter: DataFilter
): boolean => {
  switch (type) {
    case 'passage_time':
      // 通過時刻は通過リンクが指定されている場合のみ集計できる
      return dataFilter.pass.wayIds.length > 0;
    case 'staying_population':
      // 滞在人口は出発地・通過地のフィルターと併用できない
      return isEmptyFilter(dataFilter.start) && isEmptyFilter(dataFilter.pass);
    default:
      return true;
  }
};

const useTripHistogramData = (
  type: TripHistogramType
): UseTripHistogramData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { dataFilter } = useDataFilter();
  const { apiFilter } = useApiDataFilter();

  // 時間系データではヒストグラムにはフィルターせずに全範囲を表示したいため条件から除外する
  const { valueType, rangeFilterType, apiFilterKey } = HISTOGRAM_CONFIGS[type];
  const filter =
    valueType === 'time'
      ? { ...apiFilter, [apiFilterKey]: undefined }
      : apiFilter;
  const histogramBucket = getNiceHistogramBucket(
    valueType,
    dataFilter[rangeFilterType]
  );
  const isRequired = shouldFetch(type, dataFilter);

  const { data, isFetching } = useQuery({
    queryKey: [
      'tripHistogram',
      currentSimDataId,
      filter,
      type,
      histogramBucket,
    ],
    queryFn:
      currentSimDataId && isRequired
        ? async ({ signal }: QueryFunctionContext): Promise<HistogramData> => {
            return dataApiClient.getTripHistAggr(
              currentSimDataId,
              filter,
              type,
              'count' in histogramBucket
                ? { type: 'BucketCount', bucketCount: histogramBucket.count }
                : { type: 'BucketWidth', bucketWidth: histogramBucket.step },
              signal
            );
          }
        : skipToken,
    placeholderData: keepPreviousData,
    meta: { dataType: 'others' },
  });

  return {
    isLoading: isFetching,
    mean: isRequired ? (data?.mean ?? null) : null,
    median: isRequired ? (data?.median ?? null) : null,
    items: data && isRequired ? data.items : EMPTY_DATA,
  };
};

export { useTripHistogramData };
